import type { ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Typography } from '@mui/material';

import { cardSx, chipSx, groupTitleSx, wf } from './tokens';
import type { PayerRow } from './types';

export interface IntakePayerTableProps {
  canManage?: boolean;
  derivedSentence?: string | null;
  help?: ReactNode;
  loading?: boolean;
  onRemove?: (rowId: string) => void;
  rows: PayerRow[];
  search?: ReactNode;
}

export function IntakePayerTable({
  canManage = false,
  derivedSentence,
  help,
  loading = false,
  onRemove,
  rows,
  search,
}: IntakePayerTableProps) {
  return (
    <Box sx={{ ...cardSx, gridColumn: '1 / -1' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '10px' }}>
        <Box sx={groupTitleSx}>Your payer list</Box>
        {help}
        <Box sx={chipSx('mut')}>
          {canManage ? 'staff can edit' : 'managed by your JotPsych team'}
        </Box>
      </Box>

      {canManage && search && <Box sx={{ maxWidth: 480, mb: 1.5 }}>{search}</Box>}

      {loading ? (
        <Typography role="status" sx={{ color: wf.muted, fontSize: '12.5px', py: 2 }}>
          Loading payer list…
        </Typography>
      ) : rows.length === 0 ? (
        <Typography sx={{ color: wf.muted, fontSize: '12.5px', py: 2 }}>
          {canManage
            ? 'No payers configured. Search above to add the first payer.'
            : 'No payers configured. Please contact your JotPsych representative to add payers.'}
        </Typography>
      ) : (
        <Box component="section" aria-labelledby="payer-roster-table-heading">
          <Typography
            component="h3"
            id="payer-roster-table-heading"
            sx={{ color: wf.green, fontSize: '10.5px', fontWeight: 700, mb: 0.25 }}
          >
            Practice payer roster
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              aria-labelledby="payer-roster-table-heading"
              sx={{
                width: '100%',
                minWidth: 420,
                borderCollapse: 'collapse',
                fontSize: '12.5px',
              }}
            >
              <Box component="thead">
                <Box component="tr">
                  {[
                    ['Insurance company', '70%'],
                    ['Payer ID', '30%'],
                  ].map(([heading, width]) => (
                    <Box
                      component="th"
                      key={heading}
                      scope="col"
                      sx={{
                        borderBottom: `1px solid ${wf.line}`,
                        color: wf.muted,
                        fontSize: '10px',
                        letterSpacing: '.06em',
                        p: '6px 8px',
                        textAlign: 'left',
                        textTransform: 'uppercase',
                        width,
                      }}
                    >
                      {heading}
                    </Box>
                  ))}
                  {canManage && <Box component="th" aria-label="Actions" />}
                </Box>
              </Box>
              <Box component="tbody">
                {rows.map(row => (
                  <Box component="tr" key={row.rowId}>
                    <Box
                      component="td"
                      sx={{ borderBottom: `1px solid ${wf.lineSoft}`, p: '7px 8px' }}
                    >
                      {row.displayName}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        borderBottom: `1px solid ${wf.lineSoft}`,
                        color: wf.muted,
                        p: '7px 8px',
                      }}
                    >
                      {row.payerId}
                    </Box>
                    {canManage && (
                      <Box component="td" sx={{ borderBottom: `1px solid ${wf.lineSoft}`, p: 0 }}>
                        <IconButton
                          aria-label={`Remove ${row.displayName}`}
                          onClick={() => onRemove?.(row.rowId)}
                          size="small"
                          sx={{ color: wf.faint }}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <Typography sx={{ color: wf.muted, fontSize: '11.5px', mt: 1.25 }}>
        Provider enrollment status is tracked separately for each payer and service.
      </Typography>

      {derivedSentence && (
        <Box
          sx={{
            mt: 1,
            fontSize: '11.5px',
            color: wf.amberWarn,
            bgcolor: wf.amberWarnSoft,
            borderRadius: '6px',
            p: '8px 10px',
          }}
        >
          {derivedSentence}
        </Box>
      )}
    </Box>
  );
}
